import React from 'react'
import type {
    ReconciliationSummaryVO,
    ReconciliationDataCoverageVO,
    ReconciliationPropertyVO,
    ReconciliationIssueType,
    ReconciliationUiHintsVO,
} from '../../../apis/types'
import { issueColorKeyStyles } from './reconciliation.types'

interface ReconciliationSummaryProps {
    summary: ReconciliationSummaryVO
    dataCoverage: ReconciliationDataCoverageVO
    property: ReconciliationPropertyVO
    issueCountsByType: Record<string, number>
    uiHints?: ReconciliationUiHintsVO
    onIssueClick: (issueType: ReconciliationIssueType) => void
}

const ReconciliationSummary: React.FC<ReconciliationSummaryProps> = ({
    summary,
    dataCoverage,
    property,
    issueCountsByType,
    uiHints,
    onIssueClick,
}) => {
    const { readinessScore } = summary
    const pct = readinessScore.percent

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Readiness Score */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Readiness Score</div>
                <div className="flex items-end gap-2">
                    <span className="text-4xl font-bold text-indigo-900 dark:text-slate-100">{readinessScore.numerator}</span>
                    <span className="text-lg text-slate-400 mb-1">/ {readinessScore.denominator} accounts reconciled</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 mt-3">
                    <div className="bg-indigo-600 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    {summary.rowsWithIssues > 0
                        ? `${summary.rowsWithIssues} account${summary.rowsWithIssues !== 1 ? 's' : ''} with unresolved issues`
                        : 'All accounts fully reconciled'}
                </div>
            </div>

            {/* Data Sources */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Data Sources</div>
                <div className="space-y-2.5">
                    <SourceRow
                        label="OneBill"
                        detail="account & meter data"
                        available={dataCoverage.adminAvailable}
                        count={dataCoverage.totalAccountCount}
                        total={dataCoverage.totalAccountCount}
                    />
                    <SourceRow
                        label={`${property.utilityShortName ?? 'Utility'} Portal`}
                        detail="scraper: payments, autopay, service IDs"
                        available={dataCoverage.utilityPortalAvailable}
                        count={dataCoverage.scraperLinkedCount}
                        total={dataCoverage.totalAccountCount}
                        countSuffix="linked"
                    />
                    <SourceRow
                        label="Salesforce"
                        detail="VNEM allocation & addresses"
                        available={dataCoverage.salesforceAvailable}
                        count={dataCoverage.vnemAllocatedCount}
                        total={dataCoverage.totalAccountCount}
                        countSuffix="allocated"
                    />
                </div>
                {(dataCoverage.portalOnlyAccountCount > 0 || dataCoverage.hiddenAccountCount > 0) && (
                    <>
                        <div className="border-t border-slate-200 dark:border-slate-700 mt-3 pt-2 space-y-1">
                            {dataCoverage.portalOnlyAccountCount > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-amber-700 dark:text-amber-400">Portal-only (unlinked)</span>
                                    <span className="text-amber-700 dark:text-amber-400 font-medium">{dataCoverage.portalOnlyAccountCount}</span>
                                </div>
                            )}
                            {dataCoverage.hiddenAccountCount > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-slate-500 dark:text-slate-400">Intentionally excluded</span>
                                    <span className="text-slate-500 dark:text-slate-400 font-medium">{dataCoverage.hiddenAccountCount}</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
                {dataCoverage.scraperLastRun && (
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-3">
                        Scraper last ran: {new Date(dataCoverage.scraperLastRun).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                )}
            </div>

            {/* Issues Found */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-5">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Issues Found</div>
                {Object.keys(issueCountsByType).length === 0 ? (
                    <div className="text-sm text-slate-500 dark:text-slate-400">No issues detected.</div>
                ) : (
                    <div className="space-y-1.5">
                        {Object.entries(issueCountsByType).map(([issueType, count]) => {
                            const hint = uiHints?.issueTypes?.[issueType]
                            const colorKey = hint?.colorKey ?? 'gray'
                            const colors = issueColorKeyStyles[colorKey]
                            const label = hint?.label ?? issueType

                            return (
                                <button
                                    key={issueType}
                                    onClick={() => onIssueClick(issueType as ReconciliationIssueType)}
                                    className="w-full flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded px-2 py-1 -mx-2 transition-colors group"
                                >
                                    <span className="text-sm text-slate-700 dark:text-slate-300 group-hover:text-indigo-700 dark:group-hover:text-indigo-400">{label}</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${colors.bg} ${colors.text} ${colors.ring}`}>
                                        {count}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

interface SourceRowProps {
    label: string
    detail: string
    count?: number
    total?: number
    countSuffix?: string
    available: boolean
}

const SourceRow: React.FC<SourceRowProps> = ({ label, detail, count, total, countSuffix, available }) => {
    const hasCount = count != null && total != null
    const isPartial = hasCount && count < total && count > 0
    const dotColor = !available ? 'bg-slate-400' : isPartial ? 'bg-yellow-500' : 'bg-green-500'

    const badgeText = hasCount
        ? `${count} / ${total}${countSuffix ? ` ${countSuffix}` : ''}`
        : available ? 'Available' : 'Unavailable'

    const badgeStyle = !available
        ? 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 ring-slate-300 dark:ring-slate-600'
        : isPartial
            ? 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-700 dark:text-yellow-400 ring-yellow-600/10 dark:ring-yellow-400/20'
            : 'bg-green-100 dark:bg-green-400/10 text-green-700 dark:text-green-400 ring-green-600/10 dark:ring-green-400/20'

    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                <span className="text-sm dark:text-slate-300">
                    {label} <span className="text-slate-400 dark:text-slate-500 text-xs">({detail})</span>
                </span>
            </div>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${badgeStyle}`}>
                {badgeText}
            </span>
        </div>
    )
}

export default ReconciliationSummary
